import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, Sun } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_solar-energy-savings/artifacts/4q9fuk03_SIMBOLO%20SOLISY.png";

const navLinks = [
  { label: "Assinatura", href: "#geracao-distribuida" },
  { label: "Painéis Solares", href: "#instalacao" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Contato", href: "#contato" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm border-b border-slate-100"
          : "bg-[#0c1b3a]/50 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="flex items-center gap-2" data-testid="header-logo">
            <img src={LOGO_URL} alt="Solisy" className="h-10 md:h-12 w-auto" />
            <span className={`text-xl font-bold tracking-tight transition-colors duration-300 ${scrolled ? "text-[#1a4fa0]" : "text-white"}`}>
              SOLISY
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className={`text-sm font-medium transition-colors duration-200 relative after:absolute after:bottom-[-4px] after:left-0 after:w-0 after:h-[2px] after:bg-[#f5b731] hover:after:w-full after:transition-all after:duration-300 ${
                  scrolled ? "text-slate-600 hover:text-[#1a4fa0]" : "text-white/70 hover:text-white"
                }`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* CTA */}
          <div className="hidden md:block">
            <Button
              data-testid="header-cta"
              onClick={() => scrollTo("#contato")}
              className="rounded-full bg-[#f5b731] hover:bg-[#e0a31f] text-[#0f172a] px-6 h-10 text-sm font-semibold shadow-lg hover:shadow-xl transition-shadow duration-300"
            >
              <Sun className="w-4 h-4 mr-1" />
              Falar Conosco
            </Button>
          </div>

          {/* Mobile menu toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden p-2 ${scrolled ? "text-slate-700" : "text-white"}`}
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden bg-white/95 backdrop-blur-xl border-t border-slate-100 shadow-lg"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => scrollTo(link.href)}
                className="block w-full text-left px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                {link.label}
              </button>
            ))}
            <Button
              onClick={() => scrollTo("#contato")}
              className="w-full rounded-full bg-[#1a4fa0] hover:bg-[#153e80] text-white mt-2"
            >
              Falar Conosco
            </Button>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};
